import React, { useState, useEffect, useCallback } from 'react';

// ── SERP feature badge colors ─────────────────────────────────────────────────
const FEATURE_COLORS = {
  'Featured Snippet': { bg: '#fef9c3', text: '#ca8a04', icon: '⭐' },
  'Local Pack':       { bg: '#dcfce7', text: '#16a34a', icon: '📍' },
  'People Also Ask':  { bg: '#dbeafe', text: '#2563eb', icon: '❓' },
  'Paid Ads':         { bg: '#fee2e2', text: '#dc2626', icon: '💰' },
  'Image Pack':       { bg: '#f3e8ff', text: '#9333ea', icon: '🖼' },
  'Video Carousel':   { bg: '#ffedd5', text: '#ea580c', icon: '▶️' },
  'Knowledge Panel':  { bg: '#e0f2fe', text: '#0284c7', icon: '📊' },
};

// ── Favicon component ─────────────────────────────────────────────────────────
function Favicon({ domain }) {
  const [error, setError] = useState(false);
  if (error || !domain) {
    return <div style={{ width: 16, height: 16, borderRadius: 3, background: '#e2e8f0', flexShrink: 0 }} />;
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
      width={16} height={16}
      style={{ borderRadius: 3, flexShrink: 0 }}
      onError={() => setError(true)}
      alt=""
    />
  );
}

// ── Rank badge ────────────────────────────────────────────────────────────────
function RankBadge({ rank, type, isClient }) {
  if (isClient) return (
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>#{rank}</span>
    </div>
  );
  if (type === 'paid') return (
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: '#dc2626' }}>AD</span>
    </div>
  );
  if (type === 'local_pack') return (
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 10 }}>📍</span>
    </div>
  );
  return (
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: rank <= 3 ? '#f0fdf4' : '#f8fafc', border: `2px solid ${rank <= 3 ? '#86efac' : '#e2e8f0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: rank <= 3 ? '#16a34a' : '#64748b' }}>#{rank}</span>
    </div>
  );
}

// ── Single SERP result row ────────────────────────────────────────────────────
function SerpResultRow({ item, clientDomain }) {
  const isClient = item.is_client;
  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: 10,
      border: `1.5px solid ${isClient ? '#667eea' : '#e2e8f0'}`,
      background: isClient ? '#f0f4ff' : '#fff',
      marginBottom: 8,
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <RankBadge rank={item.rank} type={item.type} isClient={isClient} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Domain row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <Favicon domain={item.domain} />
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: isClient ? 700 : 400 }}>
              {item.domain || '—'}
            </span>
            {isClient && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#667eea', color: '#fff', fontWeight: 700, marginLeft: 2 }}>
                YOUR SITE
              </span>
            )}
            {item.type === 'paid' && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#fee2e2', color: '#dc2626', fontWeight: 700 }}>
                AD
              </span>
            )}
            {item.is_featured_snippet && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#fef9c3', color: '#ca8a04', fontWeight: 700 }}>
                ⭐ Featured
              </span>
            )}
          </div>

          {/* Title */}
          <a href={item.url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 14, fontWeight: 600, color: '#1a0dab', textDecoration: 'none', display: 'block', marginBottom: 3, lineHeight: 1.3 }}
            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
            onMouseLeave={e => e.target.style.textDecoration = 'none'}>
            {item.title || item.url || '—'}
          </a>

          {/* Breadcrumb / URL */}
          {item.breadcrumb && (
            <div style={{ fontSize: 11, color: '#188038', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.breadcrumb}
            </div>
          )}

          {/* Description */}
          {item.description && (
            <p style={{ fontSize: 12, color: '#4d5156', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {item.description}
            </p>
          )}

          {/* Local pack extras */}
          {item.type === 'local_pack' && (
            <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
              {item.rating && (
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  ⭐ {item.rating} {item.rating_count ? `(${item.rating_count})` : ''}
                </span>
              )}
              {item.address && <span style={{ fontSize: 11, color: '#64748b' }}>📍 {item.address}</span>}
              {item.phone && <span style={{ fontSize: 11, color: '#64748b' }}>📞 {item.phone}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main SerpPanel component ──────────────────────────────────────────────────
function SerpPanel({ keyword, websiteId, clientDomain, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('organic');
  const [refreshing, setRefreshing] = useState(false);

  const fetchSerp = useCallback(async (forceRefresh = false) => {
    if (!keyword || !websiteId) return;
    forceRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const params = new URLSearchParams({ keyword, website_id: websiteId, country_code: 'US', force_refresh: forceRefresh });
      const res = await fetch(`/keyword-intelligence/api/v1/serp/lookup?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      if (result.error) throw new Error(result.error);
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [keyword, websiteId]);

  useEffect(() => {
    fetchSerp(false);
  }, [fetchSerp]);

  // Keyboard close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const organicItems = data?.items?.filter(i => i.type === 'organic') || [];
  const localItems   = data?.items?.filter(i => i.type === 'local_pack') || [];
  const paidItems    = data?.items?.filter(i => i.type === 'paid') || [];
  const allItems     = data?.items || [];
  const clientRank   = allItems.find(i => i.is_client)?.rank || null;

  const tabs = [
    { id: 'organic', label: `Organic (${organicItems.length})` },
    ...(localItems.length   ? [{ id: 'local',   label: `Local Pack (${localItems.length})` }] : []),
    ...(paidItems.length    ? [{ id: 'paid',     label: `Ads (${paidItems.length})` }] : []),
    ...(data?.people_also_ask?.length ? [{ id: 'paa', label: `PAA (${data.people_also_ask.length})` }] : []),
  ];

  const activeItems = activeTab === 'organic' ? organicItems
    : activeTab === 'local' ? localItems
    : activeTab === 'paid' ? paidItems
    : [];

  // Cache age display
  const cacheAge = data?.cached_at ? (() => {
    const mins = Math.round((Date.now() - new Date(data.cached_at).getTime()) / 60000);
    if (mins < 60) return `${mins}m ago`;
    return `${Math.round(mins / 60)}h ago`;
  })() : null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, backdropFilter: 'blur(2px)' }} />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: '#fff', zIndex: 1001, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1.5px solid #e2e8f0', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                🔍 Live SERP Results
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', wordBreak: 'break-word', lineHeight: 1.3 }}>
                {keyword}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 12 }}>
              ✕
            </button>
          </div>

          {/* Stats row */}
          {data && !loading && (
            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              {clientRank ? (
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Your Rank</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>#{clientRank}</div>
                </div>
              ) : (
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Your Rank</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>Not in top 10</div>
                </div>
              )}
              <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Results</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{allItems.length}</div>
              </div>
              {data.serp_features?.length > 0 && (
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '6px 12px' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>SERP Features</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {data.serp_features.map(f => {
                      const fc = FEATURE_COLORS[f] || { bg: 'rgba(255,255,255,0.2)', text: '#fff', icon: '•' };
                      return (
                        <span key={f} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 600 }}>
                          {fc.icon} {f}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cache info + refresh */}
        {data && (
          <div style={{ padding: '8px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {data.cached ? `⚡ Cached ${cacheAge}` : `🔄 Just fetched`}
              {data.cost > 0 && ` · $${data.cost.toFixed(4)}`}
            </span>
            <button onClick={() => fetchSerp(true)} disabled={refreshing}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#667eea', cursor: 'pointer', fontWeight: 600 }}>
              {refreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        )}

        {/* Tabs */}
        {data && !loading && tabs.length > 0 && (
          <div style={{ display: 'flex', borderBottom: '1.5px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ flex: 1, padding: '10px 8px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: activeTab === tab.id ? '#667eea' : '#94a3b8', borderBottom: `2px solid ${activeTab === tab.id ? '#667eea' : 'transparent'}`, transition: 'all 0.15s' }}>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
              <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Fetching live SERP results...</p>
              <p style={{ color: '#94a3b8', fontSize: 12, margin: 0 }}>Powered by DataForSEO</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
              <p style={{ color: '#ef4444', fontWeight: 600 }}>{error}</p>
              <button onClick={() => fetchSerp(false)} style={{ marginTop: 12, padding: '8px 20px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Try Again
              </button>
            </div>
          ) : data ? (
            <>
              {/* Organic / Local / Paid results */}
              {activeTab !== 'paa' && (
                activeItems.length > 0 ? activeItems.map((item, i) => (
                  <SerpResultRow key={i} item={item} clientDomain={clientDomain} />
                )) : (
                  <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No results for this tab</div>
                )
              )}

              {/* People Also Ask */}
              {activeTab === 'paa' && data.people_also_ask?.length > 0 && (
                <div>
                  {data.people_also_ask.map((paa, i) => (
                    <div key={i} style={{ marginBottom: 10, padding: '12px 14px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>❓ {paa.question}</div>
                      {paa.answer && <p style={{ fontSize: 12, color: '#4d5156', margin: '0 0 6px', lineHeight: 1.5 }}>{paa.answer}</p>}
                      {paa.url && (
                        <a href={paa.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 11, color: '#667eea', textDecoration: 'none' }}>
                          {paa.domain} →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Related searches */}
              {data.related_searches?.length > 0 && activeTab === 'organic' && (
                <div style={{ marginTop: 16, padding: '12px 14px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>🔗 Related Searches</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.related_searches.map((rs, i) => (
                      <span key={i} style={{ fontSize: 12, padding: '4px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, color: '#374151', cursor: 'default' }}>
                        {rs}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
          Live data via DataForSEO · Results cached 24 hours · Press Esc to close
        </div>
      </div>
    </>
  );
}

export default SerpPanel;
